import { Download, Lock, Plus, Trash2 } from "lucide-react"
import { useEffect, useId, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"

import { useLocale } from "@/core/i18n/locale-context"
import {
  InvoiceDocument,
  InvoiceDocumentStyles,
} from "@/invoicing/components/invoice-document/invoice-document"
import { addDays } from "@/invoicing/editor-model"
import { Button } from "@/ui/button"
import { Checkbox } from "@/ui/checkbox"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"

import { INVOICE_GENERATOR_COPY } from "./invoice-generator-copy"
import {
  emptyGeneratorForm,
  generate,
  type GeneratorForm,
  type GeneratorLine,
} from "./invoice-generator-model"

/**
 * Printing: a copy of the invoice lives in a portal on `<body>`, hidden on screen. In print every
 * other child of `<body>` is removed with `display: none`, so "Save as PDF" gives a one-page
 * invoice and not the whole marketing page.
 */
const PRINT_CSS = `
#invoice-print-root { display: none; }
@media print {
  body > *:not(#invoice-print-root) { display: none !important; }
  #invoice-print-root { display: block; }
  @page { margin: 12mm; }
}
`

interface Problem {
  /** Element id of the input to focus and mark invalid. */
  target: string
  message: string
}

const inputSize = "max-md:h-11"

const Field = ({ label, id, children }: { label: string; id: string; children: ReactNode }) => (
  <div className="grid gap-1.5">
    <Label htmlFor={id}>{label}</Label>
    {children}
  </div>
)

interface InvoiceGeneratorProps {
  /** `YYYY-MM-DD`. Left out in the page: the browser fills in today's date after load. */
  today?: string
}

function initialForm(today?: string): GeneratorForm {
  if (today) return emptyGeneratorForm(today)
  return { ...emptyGeneratorForm("2000-01-01"), issueDate: "", dueDate: "" }
}

const problemId = (target: string) => `${target}-problem`
const focusSoon = (target: string) =>
  requestAnimationFrame(() => document.getElementById(target)?.focus())
const pad = (n: number) => String(n).padStart(2, "0")

export function InvoiceGenerator({ today }: InvoiceGeneratorProps) {
  const id = useId()
  const copy = INVOICE_GENERATOR_COPY[useLocale()]
  const [form, setForm] = useState(() => initialForm(today))
  const [attempted, setAttempted] = useState(false)
  const [nextKey, setNextKey] = useState(1)
  const [printRoot, setPrintRoot] = useState<HTMLElement | null>(null)

  useEffect(() => {
    // Post-hydration reads of the browser: the portal target, and today's date (rendering "today" on
    // the server would bake the build date into the prerendered HTML).
    // oxlint-disable-next-line react/set-state-in-effect
    setPrintRoot(document.body)
    if (today) return
    const d = new Date()
    const now = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    setForm((f) => (f.issueDate ? f : { ...f, issueDate: now, dueDate: addDays(now, 14) }))
  }, [today])

  const set = <K extends keyof GeneratorForm>(key: K, value: GeneratorForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }))
  const setLine = (key: string, patch: Partial<GeneratorLine>) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) }))

  const ids = {
    sellerName: `${id}-bn`,
    sellerAbn: `${id}-abn`,
    buyer: `${id}-bu`,
    number: `${id}-no`,
    issueDate: `${id}-is`,
    dueDate: `${id}-du`,
    addItem: `${id}-add`,
    description: (key: string) => `${id}-d-${key}`,
    quantity: (key: string) => `${id}-q-${key}`,
    price: (key: string) => `${id}-p-${key}`,
  }

  const { document: invoice, issues, lineErrors, fieldErrors } = generate(form)
  const problems: Problem[] = [
    ...issues.map((issue) => ({
      target: {
        seller_name: ids.sellerName,
        seller_abn: ids.sellerAbn,
        buyer: ids.buyer,
        lines: ids.addItem,
      }[issue.field],
      message: copy.messages[issue.field],
    })),
    ...(["number", "issueDate", "dueDate"] as const).flatMap((field) =>
      fieldErrors[field]
        ? [
            {
              target: ids[field],
              message: copy.fieldErrors[fieldErrors[field]] ?? fieldErrors[field],
            },
          ]
        : [],
    ),
    ...form.lines.flatMap((line, i) => {
      const found: Problem[] = []
      if (!line.description.trim()) {
        found.push({ target: ids.description(line.key), message: copy.describeItem(i + 1) })
      }
      const error = lineErrors[line.key]
      if (error === "quantity") {
        found.push({
          target: ids.quantity(line.key),
          message: copy.quantityError(i + 1),
        })
      }
      if (error === "amount") {
        found.push({
          target: ids.price(line.key),
          message: copy.amountError(i + 1),
        })
      }
      return found
    }),
  ]
  /** Props that tie an input to its error message once the user has tried to download. */
  const invalid = (target: string) => {
    const bad = attempted && problems.some((p) => p.target === target)
    return bad ? { "aria-invalid": true, "aria-describedby": problemId(target) } : {}
  }

  const download = () => {
    setAttempted(true)
    const first = problems[0]
    if (first) focusSoon(first.target) // after the re-render adds aria-invalid
    else window.print()
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
      <style>{PRINT_CSS}</style>
      <InvoiceDocumentStyles />
      {printRoot &&
        createPortal(
          <div id="invoice-print-root" lang="en" aria-hidden="true">
            <InvoiceDocument data={invoice} />
          </div>,
          printRoot,
        )}
      <div className="bg-card border-border grid content-start gap-3 rounded-lg border p-4 md:p-5">
        <h3 className="text-lg font-semibold">{copy.yourDetails}</h3>
        <Field label={copy.businessName} id={ids.sellerName}>
          <Input
            id={ids.sellerName}
            className={inputSize}
            value={form.sellerName}
            autoComplete="organization"
            onChange={(e) => set("sellerName", e.target.value)}
            {...invalid(ids.sellerName)}
          />
        </Field>
        <Field label={copy.abn} id={ids.sellerAbn}>
          <Input
            id={ids.sellerAbn}
            className={inputSize}
            value={form.sellerAbn}
            inputMode="numeric"
            placeholder={copy.abnPlaceholder}
            onChange={(e) => set("sellerAbn", e.target.value)}
            {...invalid(ids.sellerAbn)}
          />
        </Field>
        <div className="border-border flex items-center justify-between gap-3 rounded-lg border p-3">
          <div>
            <p id={`${id}-gst`} className="font-semibold">
              {copy.registeredForGst}
            </p>
            <p id={`${id}-gst-hint`} className="text-muted-foreground text-xs">
              {copy.registeredHint}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.gstRegistered}
            aria-labelledby={`${id}-gst`}
            aria-describedby={`${id}-gst-hint`}
            onClick={() => set("gstRegistered", !form.gstRegistered)}
            className="focus-visible:ring-ring group flex min-h-11 min-w-14 shrink-0 cursor-pointer items-center justify-center rounded-full outline-none focus-visible:ring-2"
          >
            <span
              aria-hidden="true"
              className="bg-input group-aria-checked:bg-primary relative h-7 w-12 rounded-full"
            >
              <span className="bg-background absolute top-0.5 left-0.5 size-6 rounded-full shadow transition-transform group-aria-checked:translate-x-5 motion-reduce:transition-none" />
            </span>
          </button>
        </div>

        <h3 className="mt-2 text-lg font-semibold">{copy.customerAndInvoice}</h3>
        <Field label={copy.customerName} id={ids.buyer}>
          <Input
            id={ids.buyer}
            className={inputSize}
            value={form.buyerName}
            onChange={(e) => set("buyerName", e.target.value)}
            {...invalid(ids.buyer)}
          />
        </Field>
        <Field label={copy.customerAbn} id={`${id}-bua`}>
          <Input
            id={`${id}-bua`}
            className={inputSize}
            value={form.buyerAbn}
            inputMode="numeric"
            onChange={(e) => set("buyerAbn", e.target.value)}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={copy.invoiceNumber} id={ids.number}>
            <Input
              id={ids.number}
              className={inputSize}
              value={form.number}
              onChange={(e) => set("number", e.target.value)}
              {...invalid(ids.number)}
            />
          </Field>
          <Field label={copy.issueDate} id={ids.issueDate}>
            <Input
              id={ids.issueDate}
              className={inputSize}
              type="date"
              value={form.issueDate}
              onChange={(e) => set("issueDate", e.target.value)}
              {...invalid(ids.issueDate)}
            />
          </Field>
          <Field label={copy.dueDate} id={ids.dueDate}>
            <Input
              id={ids.dueDate}
              className={inputSize}
              type="date"
              value={form.dueDate}
              onChange={(e) => set("dueDate", e.target.value)}
              {...invalid(ids.dueDate)}
            />
          </Field>
        </div>

        <h3 className="mt-2 text-lg font-semibold">{copy.items}</h3>
        {form.gstRegistered && (
          <fieldset className="grid gap-1.5">
            <legend className="mb-1.5 text-sm font-medium">{copy.pricesYouEnter}</legend>
            <div className="bg-muted grid grid-cols-2 gap-1 rounded-md p-1">
              {(["exclusive", "inclusive"] as const).map((value) => (
                <label
                  key={value}
                  className="has-[:checked]:bg-card has-[:checked]:border-border has-focus-visible:ring-ring flex min-h-11 cursor-pointer items-center justify-center rounded border border-transparent text-sm font-medium has-focus-visible:ring-2"
                >
                  <input
                    type="radio"
                    name={`${id}-amounts`}
                    className="sr-only"
                    checked={form.amountsAre === value}
                    onChange={() => set("amountsAre", value)}
                  />
                  {value === "exclusive" ? copy.excludeGst : copy.includeGst}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        {form.lines.map((line, i) => (
          <div key={line.key} className="border-border grid gap-3 rounded-lg border p-3">
            <Field label={copy.description} id={ids.description(line.key)}>
              <Input
                id={ids.description(line.key)}
                className={inputSize}
                value={line.description}
                onChange={(e) => setLine(line.key, { description: e.target.value })}
                {...invalid(ids.description(line.key))}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={copy.quantity} id={ids.quantity(line.key)}>
                <Input
                  id={ids.quantity(line.key)}
                  className={inputSize}
                  value={line.quantity}
                  inputMode="decimal"
                  onChange={(e) => setLine(line.key, { quantity: e.target.value })}
                  {...invalid(ids.quantity(line.key))}
                />
              </Field>
              <Field label={copy.unitPrice} id={ids.price(line.key)}>
                <Input
                  id={ids.price(line.key)}
                  className={inputSize}
                  value={line.unitAmount}
                  inputMode="decimal"
                  placeholder="0.00"
                  onChange={(e) => setLine(line.key, { unitAmount: e.target.value })}
                  {...invalid(ids.price(line.key))}
                />
              </Field>
            </div>
            <div className="flex min-h-11 items-center justify-between gap-3">
              {form.gstRegistered ? (
                <Label
                  htmlFor={`${id}-f-${line.key}`}
                  className="min-h-11 flex-1 cursor-pointer gap-3"
                >
                  <Checkbox
                    id={`${id}-f-${line.key}`}
                    className="size-5"
                    checked={line.gstFree}
                    onCheckedChange={(v) => setLine(line.key, { gstFree: v === true })}
                  />
                  {copy.gstFreeItem}
                </Label>
              ) : (
                <span />
              )}
              {form.lines.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="min-h-11"
                  aria-label={copy.removeItem(i + 1)}
                  onClick={() => {
                    set(
                      "lines",
                      form.lines.filter((l) => l.key !== line.key),
                    )
                    focusSoon(ids.addItem)
                  }}
                >
                  <Trash2 aria-hidden="true" />
                  {copy.remove}
                </Button>
              )}
            </div>
          </div>
        ))}
        <Button
          id={ids.addItem}
          type="button"
          variant="outline"
          className="min-h-11"
          onClick={() => {
            const key = `line-${nextKey}`
            set("lines", [
              ...form.lines,
              { key, description: "", quantity: "1", unitAmount: "", gstFree: false },
            ])
            setNextKey(nextKey + 1)
            focusSoon(ids.description(key))
          }}
        >
          <Plus aria-hidden="true" />
          {copy.addItem}
        </Button>
      </div>

      <div className="grid gap-3">
        <p className="text-lg font-semibold">{copy.preview}</p>
        {copy.englishDocumentNote && (
          <p className="text-muted-foreground max-w-[70ch] text-sm">{copy.englishDocumentNote}</p>
        )}
        <div
          lang="en"
          className="border-border overflow-x-auto rounded-lg border max-sm:[&_.inv-doc]:p-4! max-sm:[&_.inv-head]:flex-col! max-sm:[&_.inv-meta]:flex-wrap! max-sm:[&_.inv-seller]:text-left!"
        >
          <InvoiceDocument data={invoice} />
        </div>
        {attempted && problems.length > 0 && (
          <div
            role="alert"
            className="bg-destructive/10 text-destructive border-destructive/30 rounded-lg border p-3 text-sm"
          >
            <p className="font-semibold">{copy.fixThese}</p>
            <ul className="mt-1 list-disc pl-5">
              {problems.map((p) => (
                <li key={p.message} id={problemId(p.target)}>
                  {p.message}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" size="lg" className="min-h-11" onClick={download}>
            <Download aria-hidden="true" />
            {copy.downloadPdf}
          </Button>
          <p className="text-muted-foreground text-xs">{copy.printHint}</p>
        </div>
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <Lock className="size-3.5" aria-hidden="true" />
          {copy.runsInBrowser}
        </p>
      </div>
    </div>
  )
}
